import React from 'react';
import PropTypes from 'prop-types';
import { Download } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import SectionHeader from './SectionHeader.shadcn';

/**
 * Get localized deck name based on language
 */
const getDeckName = (deck, language) => {
  if (language === 'JP' && deck.name_ja) {
    return deck.name_ja;
  }
  return deck.name;
};

/**
 * Get localized deck description based on language
 */
const getDeckDescription = (deck, language) => {
  if (language === 'JP' && deck.description_ja) {
    return deck.description_ja;
  }
  return deck.description;
};

/**
 * Preload Decks sidebar module - load sample decks
 * Using shadcn/ui components with Tailwind
 */
function PreloadDecks({
  isCollapsed,
  onToggle,
  preloadDecks,
  onLoadDeck,
  language = 'EN',
  t = (key) => null,
  hideHeader = false,
}) {
  return (
    <div className="flex flex-col gap-2 flex-1 min-h-0">
      {!hideHeader && (
        <SectionHeader
          title={t('sidebar.sampleDecks.title') || 'Sample Decks'}
          isCollapsed={isCollapsed}
          onToggle={onToggle}
        />
      )}

      {!isCollapsed && (
        <ScrollArea className="flex-1">
          <div className="flex flex-col gap-3 p-2">
            {preloadDecks.length === 0 ? (
              <p className="px-3 py-4 text-center text-sm text-muted-foreground italic">
                {t('sidebar.sampleDecks.noDecks') || 'No sample decks available yet.'}
              </p>
            ) : (
              preloadDecks.map((deck) => (
                <div 
                  key={deck.id} 
                  className={cn(
                    "flex gap-3 p-2 bg-card rounded-md shadow-sm",
                    "hover:shadow-md transition-shadow"
                  )}
                >
                  {/* Deck Image */}
                  <div className="flex-shrink-0 w-[70px] h-[98px] rounded-sm overflow-hidden bg-muted">
                    {deck.imageUrl ? (
                      <img 
                        src={deck.imageUrl} 
                        alt={getDeckName(deck, language)} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">
                        🃏
                      </div>
                    )}
                  </div>

                  {/* Deck Info */}
                  <div className="flex-1 flex flex-col gap-1 min-w-0">
                    <h4 className="font-semibold text-sm truncate">
                      {getDeckName(deck, language)}
                    </h4>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {getDeckDescription(deck, language)}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-auto self-start"
                      onClick={() => onLoadDeck(deck)}
                    >
                      <Download className="h-3 w-3 mr-1" />
                      {t('sidebar.sampleDecks.loadDeck') || 'Load Deck'}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}

PreloadDecks.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  preloadDecks: PropTypes.array.isRequired,
  onLoadDeck: PropTypes.func.isRequired,
  language: PropTypes.string,
  t: PropTypes.func,
  hideHeader: PropTypes.bool,
};

export default PreloadDecks;
